/**
 * BlogPreview — Pixel-exact replica of the coachcedar.com blog article layout.
 * Renders the same HTML structure and CSS classes as BlogArticle.tsx on the website.
 * Used in the admin editor's Preview mode.
 */

import React, { useRef, useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';

interface BlogPreviewProps {
  title: string;
  author: string;
  category: string;
  heroImageUrl: string;
  excerpt: string;
  readTime: string;
  tags: string[];
  contentHtml: string;
  slug: string;
}

// ── Category pill class mapping (matches website) ──
const categoryClass: Record<string, string> = {
  Product: 'blog-cat--product',
  Science: 'blog-cat--science',
  Stories: 'blog-cat--stories',
};

export const BlogPreview: React.FC<BlogPreviewProps> = ({
  title, author, category, heroImageUrl, excerpt, readTime, tags, contentHtml, slug,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [tocItems, setTocItems] = useState<{ id: string; text: string; level: number }[]>([]);
  const [activeId, setActiveId] = useState('');

  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(contentHtml), [contentHtml]);

  const formattedDate = new Date().toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  const catClass = categoryClass[category] || 'blog-cat--default';

  const estimatedReadTime = useMemo(() => {
    if (readTime) return readTime;
    const text = contentHtml.replace(/<[^>]+>/g, '');
    const words = text.split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  }, [contentHtml, readTime]);

  // Build TOC from rendered headings
  useEffect(() => {
    if (!contentRef.current) return;
    // Small delay to let innerHTML render
    const timer = setTimeout(() => {
      const headings = contentRef.current?.querySelectorAll('h2, h3');
      if (!headings) return;
      const items: { id: string; text: string; level: number }[] = [];
      headings.forEach((h, i) => {
        const id = h.id || `heading-${i}`;
        if (!h.id) h.id = id;
        items.push({ id, text: h.textContent || '', level: h.tagName === 'H2' ? 2 : 3 });
      });
      setTocItems(items);
    }, 100);
    return () => clearTimeout(timer);
  }, [sanitizedHtml]);

  return (
    <div className="blog-preview-shell">
      {/* Reading progress bar (static in preview) */}
      <div className="reading-progress" aria-hidden="true">
        <div className="reading-progress-bar" style={{ width: '0%' }} />
      </div>

      <main className="article-page">
        <div className="container">
          {/* Breadcrumb */}
          <nav className="article-breadcrumb" aria-label="Breadcrumb">
            <span className="article-breadcrumb-link">Blog</span>
            <span aria-hidden="true">/</span>
            <span className="article-breadcrumb-link">{category}</span>
            <span aria-hidden="true">/</span>
            <span className="article-breadcrumb-current">{title || 'Untitled'}</span>
          </nav>

          {/* Article header */}
          <header className="article-header">
            <span className={`blog-cat-pill ${catClass}`}>
              {category}
            </span>
            <h1 className="article-title">{title || 'Untitled Post'}</h1>
            <div className="article-meta">
              <span>{author}</span>
              <span className="blog-card-meta-sep">&middot;</span>
              <span>{formattedDate}</span>
              <span className="blog-card-meta-sep">&middot;</span>
              <span>{estimatedReadTime}</span>
            </div>
          </header>

          {/* Hero image */}
          {heroImageUrl && (
            <div className="article-hero-wrap">
              <img src={heroImageUrl} alt="" className="article-hero-img" />
            </div>
          )}

          {/* Article body + sidebar */}
          <div className="article-layout">
            <article className="article-body" ref={contentRef}>
              <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
            </article>

            {/* Sidebar */}
            <aside className="article-sidebar">
              {/* Table of Contents */}
              {tocItems.length > 0 && (
                <nav className="toc" aria-label="Table of contents">
                  <p className="toc-label">On this page</p>
                  <ul className="toc-list">
                    {tocItems.map(item => (
                      <li
                        key={item.id}
                        className={`toc-item ${item.level === 3 ? 'toc-item--sub' : ''} ${activeId === item.id ? 'toc-item--active' : ''}`}
                      >
                        <span className="toc-link">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {/* Share buttons (static in preview) */}
              <div className="share-buttons">
                <p className="share-label">Share</p>
                <button className="share-btn" aria-label="Copy link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
                <button className="share-btn" aria-label="Share on X">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 4l6.5 8L4 20h2l5.5-6.8L16 20h4l-6.8-8.4L20 4h-2l-5.2 6.4L8 4H4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <button className="share-btn" aria-label="Share on LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><rect x="2" y="9" width="4" height="12" stroke="currentColor" strokeWidth="1.5"/><circle cx="4" cy="4" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
                </button>
              </div>
            </aside>
          </div>

          {/* Author card */}
          <div className="article-author-card">
            <div className="article-author-avatar">CC</div>
            <div className="article-author-info">
              <strong>{author}</strong>
              <p>Coach Cedar — AI-powered fitness coaching that evolves with you.</p>
            </div>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div style={{ marginTop: '24px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {tags.map(tag => (
                <span key={tag} style={{
                  padding: '4px 12px', borderRadius: '100px', fontSize: '0.8rem',
                  background: '#f3f4f6', color: '#6b7280',
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="article-back" style={{ marginTop: '48px' }}>
            <span className="article-back-link">&larr; Back to blog</span>
          </div>
        </div>
      </main>

      {/* Exact CSS from coachcedar.com/App.css — scoped under .blog-preview-shell */}
      <style>{blogPreviewCSS}</style>
    </div>
  );
};

// ── All blog article CSS from coachcedar.com, scoped under .blog-preview-shell ──
const blogPreviewCSS = `
.blog-preview-shell {
  --cedar-primary: #1F6C4F;
  --cedar-ink: #0B1614;
  --cedar-gray: #6B7280;
  --cedar-gray-dark: #374151;
  --cedar-limestone: #F3F3F3;
  --cedar-subtle: #E8F2ED;
  --cedar-light: #2E5E34;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.blog-preview-shell .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Reading progress */
.blog-preview-shell .reading-progress { position: relative; height: 3px; pointer-events: none; }
.blog-preview-shell .reading-progress-bar { height: 100%; background: var(--cedar-primary); }

/* Article page */
.blog-preview-shell .article-page { min-height: 400px; padding: 40px 0 60px; }

/* Breadcrumb */
.blog-preview-shell .article-breadcrumb { font-size: 0.85rem; color: #9ca3af; margin-bottom: 16px; display: flex; align-items: center; gap: 6px; }
.blog-preview-shell .article-breadcrumb-link { color: var(--cedar-primary); cursor: default; }
.blog-preview-shell .article-breadcrumb-current { color: var(--cedar-gray); }

/* Header */
.blog-preview-shell .article-header { text-align: center; max-width: 720px; margin: 0 auto 40px; }
.blog-preview-shell .article-title { font-size: clamp(2rem, 4vw, 2.75rem); font-weight: 700; color: var(--cedar-ink); margin: 0 0 16px; line-height: 1.2; }
.blog-preview-shell .article-meta { font-size: 0.9rem; color: #9ca3af; }

/* Category pills */
.blog-preview-shell .blog-cat-pill { display: inline-block; padding: 4px 12px; border-radius: 100px; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 12px; }
.blog-preview-shell .blog-cat--product { background: rgba(31, 108, 79, 0.1); color: var(--cedar-primary); }
.blog-preview-shell .blog-cat--science { background: rgba(123, 160, 91, 0.15); color: #5a8a3c; }
.blog-preview-shell .blog-cat--stories { background: rgba(212, 175, 55, 0.15); color: #a68a1a; }
.blog-preview-shell .blog-cat--default { background: rgba(107, 114, 128, 0.12); color: #4b5563; }

/* Meta separator */
.blog-preview-shell .blog-card-meta-sep { color: #d1d5db; }

/* Hero */
.blog-preview-shell .article-hero-wrap { max-width: 960px; margin: 0 auto 48px; border-radius: 12px; overflow: hidden; }
.blog-preview-shell .article-hero-img { width: 100%; height: auto; display: block; }

/* Article layout (body + sidebar) */
.blog-preview-shell .article-layout { display: grid; grid-template-columns: 1fr 240px; gap: 48px; max-width: 960px; margin: 0 auto; }

/* Article body */
.blog-preview-shell .article-body { max-width: 680px; font-size: 1.1rem; line-height: 1.8; color: var(--cedar-ink); }
.blog-preview-shell .article-body h2 { font-size: 1.5rem; font-weight: 700; margin: 48px 0 16px; color: var(--cedar-ink); }
.blog-preview-shell .article-body h3 { font-size: 1.25rem; font-weight: 600; margin: 32px 0 12px; color: var(--cedar-ink); }
.blog-preview-shell .article-body p { margin: 0 0 20px; }
.blog-preview-shell .article-body a { color: var(--cedar-primary); text-decoration: underline; }
.blog-preview-shell .article-body blockquote { margin: 24px 0; padding: 0 0 0 24px; border-left: 3px solid var(--cedar-primary); font-style: italic; color: var(--cedar-gray-dark); }
.blog-preview-shell .article-body ul, .blog-preview-shell .article-body ol { margin: 0 0 20px; padding-left: 24px; }
.blog-preview-shell .article-body li { margin-bottom: 8px; }
.blog-preview-shell .article-body code { background: var(--cedar-limestone); padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
.blog-preview-shell .article-body pre { background: var(--cedar-limestone); padding: 16px; border-radius: 8px; overflow-x: auto; margin: 0 0 20px; }
.blog-preview-shell .article-body pre code { background: none; padding: 0; }
.blog-preview-shell .article-body img { max-width: 100%; height: auto; border-radius: 8px; margin: 24px 0; }

/* Blog image extension styles */
.blog-preview-shell .blog-img-figure { margin: 24px 0; }
.blog-preview-shell .blog-img-figure img { max-width: 100%; height: auto; border-radius: 8px; }
.blog-preview-shell .blog-img-figure figcaption { text-align: center; font-size: 0.85rem; color: var(--cedar-gray); font-style: italic; margin-top: 8px; }
.blog-preview-shell .blog-img--small { max-width: 33%; }
.blog-preview-shell .blog-img--medium { max-width: 50%; }
.blog-preview-shell .blog-img--full { max-width: 100%; }
.blog-preview-shell .blog-img--left { float: left; margin-right: 24px; margin-bottom: 16px; }
.blog-preview-shell .blog-img--center { margin-left: auto; margin-right: auto; display: block; }
.blog-preview-shell .blog-img--right { float: right; margin-left: 24px; margin-bottom: 16px; }

/* Sidebar */
.blog-preview-shell .article-sidebar { position: sticky; top: 20px; align-self: start; }

/* Table of Contents */
.blog-preview-shell .toc { margin-bottom: 24px; }
.blog-preview-shell .toc-label { font-size: 0.75rem; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
.blog-preview-shell .toc-list { list-style: none; padding: 0; margin: 0; }
.blog-preview-shell .toc-item { padding: 4px 0; }
.blog-preview-shell .toc-item--sub { padding-left: 12px; }
.blog-preview-shell .toc-item--active .toc-link { color: var(--cedar-primary); font-weight: 600; }
.blog-preview-shell .toc-link { font-size: 0.85rem; color: var(--cedar-gray); text-decoration: none; cursor: default; line-height: 1.5; display: block; }

/* Share buttons */
.blog-preview-shell .share-buttons { margin-bottom: 24px; }
.blog-preview-shell .share-label { font-size: 0.75rem; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
.blog-preview-shell .share-btn { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; border: 1px solid #e5e7eb; background: #fff; color: var(--cedar-gray); cursor: default; margin-right: 6px; }

/* Author card */
.blog-preview-shell .article-author-card { display: flex; align-items: center; gap: 12px; padding: 24px 0; border-top: 1px solid #e5e7eb; margin-top: 48px; max-width: 680px; }
.blog-preview-shell .article-author-avatar { width: 48px; height: 48px; border-radius: 50%; background: var(--cedar-subtle); display: flex; align-items: center; justify-content: center; font-weight: 700; color: var(--cedar-primary); font-size: 0.9rem; flex-shrink: 0; }
.blog-preview-shell .article-author-info { font-size: 0.9rem; color: var(--cedar-gray-dark); line-height: 1.5; }
.blog-preview-shell .article-author-info p { margin: 4px 0 0; }

/* Back link */
.blog-preview-shell .article-back-link { font-size: 0.9rem; color: var(--cedar-primary); text-decoration: none; font-weight: 500; cursor: default; }
`;
